import { useEffect, useState } from "react";
import { api, formatBRL } from "../../lib/api";
import { Plus, Trash2, Calculator, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import ExportButtons from "../../components/ExportButtons";

export default function Precificacao() {
  const [costs, setCosts] = useState([]);
  const [form, setForm] = useState({ name: "", type: "fixo", amount: 0, category: "" });
  const [scenario, setScenario] = useState({ desired_margin_pct: 30, target_clients: 50, average_price: 500 });
  const [result, setResult] = useState(null);

  const load = () => api.get("/costs").then(r => setCosts(r.data));
  useEffect(() => { load(); simulate(); }, []);

  const addCost = async (e) => {
    e.preventDefault();
    try {
      await api.post("/costs", { ...form, amount: +form.amount });
      toast.success("Custo adicionado");
      setForm({ name: "", type: "fixo", amount: 0, category: "" });
      load();
    } catch { toast.error("Erro"); }
  };

  const removeCost = async (id) => { await api.delete(`/costs/${id}`); load(); };

  const simulate = async () => {
    try {
      const { data } = await api.post("/pricing/simulate", scenario);
      setResult(data);
    } catch (err) { console.warn("Simulação de preço indisponível (requer admin):", err?.response?.status); }
  };

  const totalFixo = costs.filter(c => c.type === "fixo").reduce((s, c) => s + c.amount, 0);
  const totalVar = costs.filter(c => c.type === "variavel").reduce((s, c) => s + c.amount, 0);

  return (
    <div data-testid="admin-precificacao">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Precificação</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Motor de Precificação</h1>
        </div>
        <ExportButtons resource="precificacao" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Costs */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="font-display text-xl text-[#14213D] mb-4">Custos do coworking</h2>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-gray-50 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Fixo</div>
              <div className="font-display text-xl text-[#14213D]">{formatBRL(totalFixo)}</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Variável</div>
              <div className="font-display text-xl text-[#14213D]">{formatBRL(totalVar)}</div>
            </div>
          </div>

          <form onSubmit={addCost} className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="cost-form">
            <input required placeholder="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="md:col-span-2 border border-gray-200 rounded px-3 py-2 text-sm" />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm">
              <option value="fixo">Fixo</option>
              <option value="variavel">Variável</option>
            </select>
            <input required type="number" step="0.01" placeholder="Valor" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
            <button type="submit" className="bg-[#14213D] text-white rounded px-3 text-sm flex items-center justify-center gap-1" data-testid="save-cost"><Plus size={14} /> Add</button>
          </form>

          <div className="space-y-1 max-h-80 overflow-y-auto">
            {costs.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded" data-testid={`cost-${c.id}`}>
                <div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold mr-2 px-1.5 py-0.5 rounded ${c.type === "fixo" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>{c.type}</span>
                  <span className="text-sm text-[#14213D] font-medium">{c.name}</span>
                  {c.category && <span className="text-xs text-gray-400 ml-2">· {c.category}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-[#14213D]">{formatBRL(c.amount)}</span>
                  <button onClick={() => removeCost(c.id)} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`del-cost-${c.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Simulator */}
        <div className="bg-gradient-to-br from-[#14213D] to-[#1F335E] text-white rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calculator size={18} className="text-[#D4AF37]" />
            <h2 className="font-display text-xl">Simulador</h2>
          </div>

          <div className="space-y-3 mb-5">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Margem desejada (%)</label>
              <input type="number" value={scenario.desired_margin_pct} onChange={(e) => setScenario({ ...scenario, desired_margin_pct: +e.target.value })} className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Clientes alvo</label>
              <input type="number" value={scenario.target_clients} onChange={(e) => setScenario({ ...scenario, target_clients: +e.target.value })} className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Preço médio (R$)</label>
              <input type="number" value={scenario.average_price} onChange={(e) => setScenario({ ...scenario, average_price: +e.target.value })} className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm mt-1" />
            </div>
          </div>
          <button onClick={simulate} className="btn-gold w-full py-2.5 rounded text-sm" data-testid="simulate-btn">Simular cenário</button>

          {result && (
            <div className="mt-5 pt-5 border-t border-white/10 space-y-3" data-testid="simulation-result">
              <div className="flex justify-between"><span className="text-xs text-white/60">Receita esperada</span><span className="font-semibold text-[#D4AF37]">{formatBRL(result.expected_revenue)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-white/60">Custo total</span><span className="font-semibold">{formatBRL(result.total_cost)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-white/60">Custo por estação</span><span className="font-semibold">{formatBRL(result.cost_per_workstation)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-white/60">Contribuição/cliente</span><span className="font-semibold">{formatBRL(result.contribution_per_client)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-white/60">Lucro esperado</span><span className="font-semibold text-emerald-400">{formatBRL(result.expected_profit)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-white/60">Margem real</span><span className="font-semibold">{result.expected_margin_pct}%</span></div>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded p-3 mt-3">
                <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold mb-1">Ponto de equilíbrio</div>
                <div className="text-sm">{Math.ceil(result.break_even_clients)} clientes</div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1">Preço sugerido</div>
                <div className="font-display text-xl">{formatBRL(result.suggested_average_price)}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
